<template>
  <div class="global-nav-container">
    <Button
      :icon="collapseButtonIconString"
      @click="isGlobalNavCollapsed = !isGlobalNavCollapsed"
      class="btn-toggle-global-nav p-button-text"
    />
    <router-link to="/plans" class="logo-link">
      <img
        class="global-logo"
        alt="Annum logo"
        src="@/assets/logo-global.jpeg"
        width="126"
        height="54"
      />
    </router-link>
    <div class="nav-core">
      <router-link to="/plans" :class="{'router-link-active': isInPlansSection}"
        >Plans</router-link
      >
    </div>
    <div class="global-nav-legal">
      <div class="global-help-menu-container">
        <a class="p-button-text global-help-menu-button" style="font-size: 1.1rem; padding: 0.8rem 0;" href="https://www.annumplanning.com/customer-referral-program/" target="_blank">Referral Program</a>
        <Button
          label="Help"
          class="p-button-text global-help-menu-button"
          @click="
            (event) => {
              this.$refs.globalHelpMenu.toggle(event)
            }
          "
        />
        <OverlayPanel
          ref="globalHelpMenu"
          appendTo="body"
          class="global-help-menu"
        >
          <ul>
            <li>
              <a
                class="menu-item"
                href="https://www.annumplanning.com/marketing-resources/guides/"
                target="_blank"
                >Guides</a
              >
            </li>
            <li>
              <a
                class="menu-item"
                @click="$store.dispatch('updateFeedbackModal', true)"
                >Submit Feedback</a
              >
            </li>
            <li>
              <a class="menu-item" href="mailto:support@annumplanning.com"
                >Contact Us</a
              >
            </li>
          </ul>
        </OverlayPanel>
      </div>

      <div class="global-settings-menu-container">
        <Button
          :label="$store.getters.currentUser.initials"
          class="p-button-rounded global-settings-menu-button"
          @click="
            (event) => {
              this.$refs.globalSettingsMenu.toggle(event)
            }
          "
        />
        <OverlayPanel
          ref="globalSettingsMenu"
          appendTo="body"
          class="global-settings-menu"
        >
          <ul>
            <li>
              <template v-if="$store.getters.allAccounts.length > 1">
                <a
                  class="menu-item"
                  @click="
                    (event) => {
                      this.$refs.selectAccountOverlay.toggle(event)
                    }
                  "
                  >Account:
                  {{ $store.getters.currentAccount.contactCompanyName }}</a
                >
                <OverlayPanel ref="selectAccountOverlay" appendTo="body">
                  <div
                    v-for="account of $store.getters.allAccounts"
                    :key="account.id.intID"
                    class="p-d-flex p-ai-center p-jc-between p-mb-2"
                  >
                    <h4 style="margin-bottom: 0" class="p-mr-3">
                      {{
                        account.contactCompanyName == ''
                          ? 'Account ' + account.id.intID
                          : account.contactCompanyName
                      }}
                    </h4>
                    <Button
                      v-if="
                        account.id.intID !==
                        $store.getters.currentAccount.id.intID
                      "
                      icon="pi pi-arrow-right"
                      @click="clickSwitchAccount(account)"
                    ></Button>
                  </div>
                </OverlayPanel>
              </template>
              <template v-else>
                <span class="menu-item"
                  >Account:
                  {{ $store.getters.currentAccount.contactCompanyName }}</span
                >
              </template>
            </li>
            <li v-if="this.$store.getters.currentAccount.isActive || this.$store.getters.currentUser.isSuperUser">
              <a
                v-if="this.canUserAccessAccountAdminView"
                class="menu-item"
                @click="
                  (event) => {
                    $router.push('/account')
                    this.$refs.globalSettingsMenu.hide(event)
                  }
                "
                >Account Settings</a
              >
            </li>
            <li v-if="canUserAccessAccountAdminView && this.$store.getters.currentAccount.showChargebeeUI">
              <a class="menu-item" @click="clickSubscriptionButton"
                >Subscriptions</a
              >
            </li>
            <li v-if="this.$store.getters.currentAccount.isActive || this.$store.getters.currentUser.isSuperUser">
              <a
                class="menu-item"
                @click="
                  (event) => {
                    $router.push(
                      '/account/user/' + $store.getters.currentUser.id.intID
                    )
                    this.$refs.globalSettingsMenu.hide(event)
                  }
                "
                >Profile</a
              >
            </li>
            <li>
              <a class="menu-item" @click="clickLogOut">Log Out</a>
            </li>
          </ul>
        </OverlayPanel>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import Vue from 'vue'
import Button from 'primevue/button'
import Dialog from 'primevue/dialog'
import Role from '@/models/Role'
import Account from '@/models/Account'

Vue.component('Button', Button)
Vue.component('Dialog', Dialog)

export default Vue.extend({
  name: 'GlobalNavigation',
  data: () => {
    return {
      isGlobalNavCollapsed: false,
      collapseButtonIconString: 'pi pi-chevron-left',
      isChargebeeButtonMoved: false as boolean,
    }
  },
  computed: {
    isInPlansSection(): boolean {
      return this.$route.fullPath.includes('plan')
    },
    canUserAccessAccountAdminView(): boolean {
      return (
        this.$store.getters.currentAccountPermissionLevel == Role.LEVEL_ACCOUNT_OWNER || this.$store.getters.currentUserIntID === 3
      )
    },
  },
  watch: {
    isGlobalNavCollapsed: function () {
      this.collapseButtonIconString = this.isGlobalNavCollapsed
        ? 'pi pi-chevron-right'
        : 'pi pi-chevron-left'
      this.$store.commit(
        'updateIsGlobalNavCollapsed',
        this.isGlobalNavCollapsed
      )
    },
  },
  beforeCreate(){
    try {
      // Set callbacks for Chargebee integration, API reference here (https://www.chargebee.com/checkout-portal-docs/dropin-reference.html#chargebee-object)
      window['Chargebee'].getInstance().setPortalCallbacks({
        close: () => {
          this.$store.dispatch('refreshCurrentAccount')
        },
      })
    } catch (error) {
      console.warn('GlobalNavigation.beforeCreate() get Chargebee instance failed: ', error)
    }
  },
  methods: {
    clickSwitchAccount(account) {
      if(this.$route.path != '/plans'){
        this.$router.push('/plans')
      }
      this.$store.dispatch('switchAccount', account.id.intID)
    },
    clickLogOut() {
      this.$store.dispatch('logout').then(() => {
        this.$router.push('/sign-in')
      })
    },
    clickSubscriptionButton() {
      const chargebeeButtonEl = window.document.getElementById(
        'chargebee_manage_subscription_button'
      ) as HTMLElement
      if (chargebeeButtonEl) {
        chargebeeButtonEl.click()
      }
    },
  },
})
</script>

<style lang="scss">
@import '@/styles/_imports.scss';

.global-nav-collapsed {
  .global-nav-container {
    left: calc(
      -15% + 3.2rem
    ); //TODO: should use $globalNavWidth and $navCollapseButtonWidth but having issues with calc()
    transition: left 0.3s;
  }
}
.global-nav-container {
  display: flex;
  flex-direction: column;
  position: absolute;
  top: 0;
  left: 0;
  width: $globalNavWidth;
  height: 100vh;
  padding: 0 $navCollapseButtonWidth 1rem 0;
  transition: left 0.3s;

  .global-logo {
    max-width: 7vw;
  }

  .btn-toggle-global-nav.p-button:enabled {
    position: absolute;
    right: 0;
    top: 0;
    bottom: 0;
    width: $navCollapseButtonWidth;
    z-index: 1;
    background: #fff;
    border-right: 0.1rem solid $black !important;
    border-left: 0.1rem solid $black !important;
    border-radius: 0;

    &:hover {
      background: #fff;
    }
    &:focus {
      box-shadow: none;
    }
  }

  a {
    display: block;
    position: relative;
    @include font-mulish;
    color: $black;
    text-decoration: none;
    padding: 1.2rem 2.4rem;
    font-size: 1.4rem;
    line-height: 1.6rem;

    &:not(.only-exact).router-link-active,
    &.only-exact.router-link-exact-active {
      color: #fff;
      background-color: $black;
    }
    &.router-link-active.logo-link {
      background-color: transparent;
    }
  }

  .nav-core {
    margin-top: 0;
  }

  .global-nav-legal {
    margin-top: auto;
    display: flex;
    flex-direction: column;
    row-gap: 1rem;
    padding: 0.8rem 2.4rem;

    .global-help-menu-button {
      padding-right: 0;
      padding-left: 0;
      color: $dim-gray;

      &:enabled:hover {
        background-color: transparent;
        color: $dim-gray;
      }
    }
    .global-settings-menu-button {
      display: flex;
      justify-content: center;
      align-content: center;
      padding: 0;
      width: 32px;
      height: 32px;
      border-radius: 50%;
    }
  }
}
.global-help-menu,
.global-settings-menu {
  &.p-overlaypanel {
    .p-overlaypanel-content {
      padding: 1.4rem 2.4rem;
    }
  }
  ul {
    margin: 0;
    padding-left: 0;
  }
  .menu-item {
    display: block;
    padding: 1rem 0;
    font-size: 1.1rem;
    color: #000;
    cursor: pointer;
  }
}
</style>
